import { CalendarDays } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, formatTime } from '@/lib/format';
import { useScheduleFeed } from '../queries';

const UPCOMING_WINDOW_DAYS = 7;

interface UpcomingLessonsCardProps {
  /** Restrict the feed to one class (class-detail usage). */
  classPublicId?: string;
}

/**
 * "Upcoming lessons" — the next 7 days of the role-aware schedule feed
 * (pupils only ever receive published lessons of their own classes). Reused on
 * the dashboard and the class detail page.
 */
export function UpcomingLessonsCard({ classPublicId }: UpcomingLessonsCardProps) {
  const { t, i18n } = useTranslation(['calendar', 'curriculum']);

  // Stable window bounds — computed once per mount so the query key doesn't
  // churn on re-renders.
  const window_ = useMemo(() => {
    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + UPCOMING_WINDOW_DAYS);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const feed = useScheduleFeed({
    ...window_,
    ...(classPublicId ? { school_class: classPublicId } : {}),
    page_size: 50,
  });

  const rows = feed.data?.results ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('calendar:upcoming.title')}</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/calendar">
              <CalendarDays />
              {t('calendar:upcoming.openCalendar')}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {feed.isLoading ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('calendar:upcoming.empty')}</p>
        ) : (
          <ul className="space-y-1">
            {rows.map(entry => (
              <li key={entry.public_id}>
                <Link
                  to={`/lessons/${entry.lesson.public_id}`}
                  className="hover:bg-accent flex flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded-md px-2 py-1.5 text-sm"
                >
                  <span className="text-muted-foreground tabular-nums">
                    {formatDate(entry.starts_at, i18n.language)} ·{' '}
                    {formatTime(entry.starts_at, i18n.language)}
                  </span>
                  <span className="font-medium">
                    {entry.lesson.number !== null
                      ? `${t('curriculum:course.lessonNumber', { n: entry.lesson.number })} `
                      : ''}
                    {entry.lesson.title}
                  </span>
                  {!classPublicId ? (
                    <span className="text-muted-foreground">{entry.school_class.name}</span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
